import classNames from 'classnames';
import React, { useMemo } from 'react';

const range = (size: number, startAt: number): number[] => {
	return [...Array(size).keys()].map((x) => x + startAt);
};

const numberOfDots = 2;
const numberOfSides = 2;
const numberOfPageButtonInMiddle = 1;

export const MaerskPagination: React.FC<IMaerskPaginationProps> = ({
	totalPages,
	activePage,
	disabledClassName = '',
	boundaryEllipsisText = '...',
	nextButtonText = 'Next',
	previousButtonText = 'Previous',
	siblingCount,
	align = 'center',
	onRenderNextButton,
	onRenderBoundaryEllipsis,
	onRenderPageButton,
	onRenderPreviousButton,
}) => {
	const totalNumberOfPageButtonsPerSides = useMemo(
		() => numberOfSides - 1 + (numberOfDots - 1) + siblingCount,
		[siblingCount]
	);
	const totalNumberOfPageButtons = useMemo(
		() => totalNumberOfPageButtonsPerSides * 2,
		[totalNumberOfPageButtonsPerSides]
	);
	const totalNumberOfPageButtonIncludingMiddle = useMemo(
		() => totalNumberOfPageButtons + numberOfPageButtonInMiddle,
		[totalNumberOfPageButtons]
	);

	const isAnyDotsVisible = useMemo(
		() => totalPages > totalNumberOfPageButtonIncludingMiddle,
		[totalNumberOfPageButtonIncludingMiddle, totalPages]
	);
	const showLeftDots = useMemo(
		() => isAnyDotsVisible && activePage > totalNumberOfPageButtonsPerSides + numberOfPageButtonInMiddle,
		[activePage, isAnyDotsVisible, totalNumberOfPageButtonsPerSides]
	);
	const showRightDots = useMemo(
		() => isAnyDotsVisible && activePage <= totalPages - totalNumberOfPageButtonsPerSides,
		[activePage, isAnyDotsVisible, totalNumberOfPageButtonsPerSides, totalPages]
	);

	const size = useMemo(
		() =>
			totalPages <= 1
				? 0
				: showLeftDots && showRightDots
				? siblingCount * 2 + numberOfPageButtonInMiddle
				: isAnyDotsVisible
				? siblingCount * 2 + numberOfPageButtonInMiddle + (numberOfDots - 1)
				: totalPages - numberOfSides,
		[isAnyDotsVisible, showLeftDots, showRightDots, siblingCount, totalPages]
	);

	const startAt = useMemo(
		() =>
			showLeftDots && showRightDots
				? activePage - siblingCount
				: showLeftDots
				? totalPages - totalNumberOfPageButtonsPerSides - siblingCount
				: showRightDots
				? 2
				: 2,
		[activePage, showLeftDots, showRightDots, siblingCount, totalNumberOfPageButtonsPerSides, totalPages]
	);

	return (
		<div className='maersk-pagination'>
			<div className={classNames('wrapper', align)}>
				{onRenderPreviousButton(
					classNames({ [disabledClassName]: activePage <= 1 }, 'previous-button'),
					activePage <= 1,
					previousButtonText
				)}
				{totalPages > 0 && onRenderPageButton(classNames({ active: activePage === 1 }, 'page-button'), 1)}
				{showLeftDots && onRenderBoundaryEllipsis('boundary-ellipsis page-button', boundaryEllipsisText)}
				{range(size, startAt).map((x) => (
					<div key={x}>{onRenderPageButton(classNames({ active: activePage === x }, 'page-button'), x)}</div>
				))}
				{showRightDots && onRenderBoundaryEllipsis('boundary-ellipsis page-button', boundaryEllipsisText)}
				{totalPages > 1 &&
					onRenderPageButton(classNames({ active: activePage === totalPages }, 'page-button'), totalPages)}
				{onRenderNextButton(
					classNames({ [disabledClassName]: activePage >= totalPages }, 'next-button'),
					activePage >= totalPages,
					nextButtonText
				)}
			</div>
		</div>
	);
};

interface IMaerskPaginationProps {
	totalPages: number;
	activePage: number;
	siblingCount?: number;
	disabledClassName?: string;
	align?: 'start' | 'end' | 'center';
	nextButtonText?: string;
	previousButtonText?: string;
	boundaryEllipsisText?: string;
	onRenderBoundaryEllipsis: (defaultClassName: string, defaultText: string) => JSX.Element;
	onRenderPreviousButton: (defaultClassName: string, isDisabled: boolean, defaultText: string) => JSX.Element;
	onRenderNextButton: (defaultClassName: string, isDisabled: boolean, defaultText: string) => JSX.Element;
	onRenderPageButton: (defaultClassName: string, pageNumber: number) => JSX.Element;
}



// WEBPACK FOOTER //
// ./src/components/shared/maersk-pagination.tsx